import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Search, Trash2, Download, UserPlus, Filter, RotateCcw, Calendar, ChevronDown, X, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import DeletePermission from "../suspence/DeletePermission";
import { CreateAccountModal } from "./CreateAccount";
import { PROTECTION_JOBS_ROUTE } from "./ProtectionJobs";
import {
  deleteProtectedAccounts,
  fetchExportProtected,
} from "../../service/Security";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import { TEXT_FILTER_DEBOUNCE_MS } from "../../utils/taskListFilters";
import datePickerPopper from "../common/DatePickerPopper";
import { isRTL } from "../../utils/rtl";
import SearchFieldPicker from "../common/SearchFieldPicker";
import CommonTooltip from "../common/CommonTooltip";
import HoverTooltip from "../common/HoverTooltip";

// "all" uses the backend's global search; a column uses a "contains" filter on it.
const SEARCH_SCOPES = [
  { value: "all", labelKey: "allFields", fallback: "All Fields" },
  { value: "accountNo", labelKey: "accno", fallback: "Account No" },
  { value: "createdByName", labelKey: "createdBy", fallback: "Created By" },
  { value: "updatedByName", labelKey: "updatedBy", fallback: "Updated By" },
  { value: "remarks", labelKey: "remark", fallback: "Remark" },
  { value: "previousRemarks", labelKey: "previousRemarks", fallback: "Previous Remarks" },
];

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

const getBackendMessageFromResponse = (response) =>
  response?.body?.value ??
  response?.body?.error ??
  response?.header?.message;

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const formatShortDate = (ymd) => {
  const d = parseYmd(ymd);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Same date-range popover as StockManagement.js -- ported locally; here the
// dates only feed the export report, so OK just closes it.
const DATE_RANGE_POPOVER_WIDTH = 300;
const DateRangeFilterPopover = ({
  anchorEl,
  onClose,
  fromDate,
  toDate,
  onChangeFrom,
  onChangeTo,
  onClear,
  onApply,
  todayPicker,
  isRtl,
  langMode,
}) => {
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - DATE_RANGE_POPOVER_WIDTH - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      // The calendar popups are portaled to document.body via datePickerPopper,
      // so they're siblings of this popover in the DOM, not descendants -- a
      // click inside one would otherwise look like an "outside" click.
      if (event.target.closest?.(".react-datepicker, .datepicker-portal")) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: DATE_RANGE_POPOVER_WIDTH }}
      className="z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].fromdate}</label>
          <DatePicker
            selected={parseYmd(fromDate)}
            onChange={onChangeFrom}
            placeholderText={translations[langMode].fromdate}
            maxDate={todayPicker}
            selectsStart
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].todate}</label>
          <DatePicker
            selected={parseYmd(toDate)}
            onChange={onChangeTo}
            placeholderText={translations[langMode].todate}
            minDate={parseYmd(fromDate)}
            maxDate={todayPicker}
            selectsEnd
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            disabled={!fromDate}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
        <button type="button" onClick={onClear} className="text-xs font-medium text-slate-500 hover:underline">
          {translations[langMode].reset || "Clear"}
        </button>
        <button data-button-intent="primary"
          type="button"
          onClick={onApply}
          className="h-8 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96]"
        >
          {translations[langMode]?.ok ?? "Ok"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export const SecurityToolbar = ({
  setAccountNo,
  hasSelectedRecords,
  selectedRecords,
  setSelectedRecords,
  setList,
  loadProtectedAccounts,
  accountNo,
  canCreateProtectedAccount,
  canDeleteProtectedAccount,
  showColumnFilters,
  setShowColumnFilters,
  hasActiveFilterOrSort,
  activeFilterOrSortCount,
  resetTableFilters,
  totalItems,
  itemsLoading,
  searchScope = "all",
  setSearchScope,
}) => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showDateRangePopover, setShowDateRangePopover] = useState(false);
  const dateRangeButtonRef = useRef(null);
  const hasDateRange = Boolean(fromDate || toDate);
  const {
    setParticularAccount,
    setIsEditRequest,
    selectedOrgId,
    theme,
    langMode,
    userType,
    projectId,
  } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [deleteItemType, setDeleteItemType] = useState("");
  const [inputValue, setInputValue] = useState(""); // Temporary state for input
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const deleteFunction = async (selectedRecords) => {
    try {
      const data = await deleteProtectedAccounts(selectedRecords);
      if (data.header && data.header.code === 600) {
        setSelectedRecords([]);
        setList((prevList) =>
          prevList.filter((item) => !selectedRecords.includes(item.id))
        );
        toast.success(data.body.value)
        setAccountNo('')
        setInputValue('')
        loadProtectedAccounts()
      } else {
        toast.error(data.body.value)
      }
    } catch (error) {
      console.error("Error:", error);
    }
    handleCloseDeleteModal();
  };

  const handleCreate = () => {
    if (userType === "super_admin" && !selectedOrgId) {
      toast.warn(translations[langMode].pleaseSelectOrganizationCreatingAccount);
      return;
    }
    setParticularAccount([]);
    setIsEditRequest(false);
    setCreateModalOpen(true);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value); // Update temporary state
  };

  const handleSearch = () => {
    if (inputValue === accountNo) {
      loadProtectedAccounts()
    }
    setAccountNo(inputValue);
  };

  // Search as the user types, like TaskListView: debounced, but clearing applies at once.
  useEffect(() => {
    if (!inputValue) {
      handleSearch();
      return undefined;
    }
    const timer = setTimeout(() => setAccountNo(inputValue), TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const scopeOptions = SEARCH_SCOPES.map((scope) => ({
    value: scope.value,
    label: translations[langMode]?.[scope.labelKey] || scope.fallback,
  }));
  const searchFieldLabel = scopeOptions.find((scope) => scope.value === searchScope)?.label ?? scopeOptions[0].label;
  const searchPlaceholder = `${translations[langMode]?.search ?? "Search"} ${searchFieldLabel}...`;

  // The typed text belonged to the previous field, so clear it, as User Management does.
  const handleScopeSelect = (value) => {
    if (value === searchScope) return;
    setSearchScope?.(value);
    setInputValue("");
  };

  // Reset also counts and clears the account search and the export date range;
  // clearing the input reloads via the effect above.
  const hasSearch = Boolean(accountNo || inputValue);
  const resetCount = activeFilterOrSortCount + (hasSearch ? 1 : 0) + (hasDateRange ? 1 : 0);
  const hasAnythingToReset = hasActiveFilterOrSort || hasSearch || hasDateRange;
  const handleResetAll = () => {
    resetTableFilters();
    setInputValue("");
    setSearchScope?.("all");
    setFromDate("");
    setToDate("");
    setShowDateRangePopover(false);
  };

  const showDeleteModal = (type, id) => {
    setDeleteItemType(type);
    setDeleteItemId(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const exportReport = async (fromDate, toDate) => {
    if (!fromDate || !toDate) {
      toast.error(translations[langMode].pleaseSelectFromDateToDate);
      return;
    }
    if (!projectId) {
      toast.error(translations[langMode].pleaseSelectProjectIdFirst);
      return;
    }
    try {
      const response = await fetchExportProtected(
        projectId,
        fromDate,
        toDate
      );
      if (response && response.header) {
        const code = response.header.code;
        if (code === 607) {
          const backendMessage = getBackendMessageFromResponse(response);
          if (backendMessage) {
            toast.error(backendMessage);
          }
        } else if (code === 600) {
          const s3Link = response.body?.value;
          if (s3Link) {
            // Navigate to the signed link instead of fetch()-ing it, so storage CORS rules cannot block the download.
            const downloadLink = document.createElement("a");
            downloadLink.href = s3Link;
            downloadLink.rel = "noopener";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            toast.success(translations[langMode].excelisdownloading);
          } else {
            const backendMessage = getBackendMessageFromResponse(response);
            if (backendMessage) {
              toast.error(backendMessage);
            }
          }
        } else {
          const backendMessage = getBackendMessageFromResponse(response);
          if (backendMessage) {
            toast.error(backendMessage);
          }
        }
      } else {
        const backendMessage = getBackendMessageFromResponse(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    }
  };

  return (
    <>
      <div
        className={`mx-2 mt-2 min-w-0 shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm flex flex-col gap-3 sm:mx-4 sm:mt-3 sm:px-5 sm:py-4 sm:gap-4 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0 flex-1 basis-52">
            <h1 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
              {translations[langMode]?.protectedAccountsTitle ?? "Protected Accounts"}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode]?.protectedAccountsSubtitle ?? "Protects accounts from automatic write-offs and closures."}
            </p>
          </div>
          <div className="flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto sm:justify-start sm:ml-auto">
            {canDeleteProtectedAccount && hasSelectedRecords && (
              <CommonButton colorIntent="danger"
                onClick={() => showDeleteModal("single", null)}
                variant="danger"
                wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              >
                <Trash2 size={16} />
                <span className="text-xs sm:text-sm font-medium">{translations[langMode].delete}</span>
              </CommonButton>
            )}
            <CommonButton colorIntent="primary"
              onClick={() => exportReport(fromDate, toDate)}
              variant="success"
              title={translations[langMode].export}
              tooltipText={translations[langMode]?.exportProtectedAccountsReport ?? "Export protected accounts report"}
              tooltipPosition="top"
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
            >
              <Download className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">{translations[langMode].export}</span>
            </CommonButton>
              <CommonTooltip
              text={translations[langMode]?.protectionJobsTooltip ?? "View the status and history of protection requests"}
              position="top"
              disableDefaultDescription
              className="flex-1 min-w-0 sm:flex-none"
              tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
              fitContent
              clampToViewport
              hoverOnly
            >
              <CommonButton colorIntent="primary"
                onClick={() => navigate(PROTECTION_JOBS_ROUTE)}
                variant="primary"
                wrapperClassName="flex w-full min-w-0 sm:inline-block sm:w-auto"
                className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
                data-testid="protection-jobs-button"
              >
                <History className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">
                  {translations[langMode]?.protectionJobsTitle ?? "Protection History"}
                </span>
              </CommonButton>
            </CommonTooltip>
            {canCreateProtectedAccount && (
              <CommonButton colorIntent="primary"
                onClick={handleCreate}
                variant="primary"
                title={translations[langMode].createproacc}
                tooltipText={translations[langMode]?.createProtectedAccountTooltip ?? "Create a protected account"}
                tooltipPosition="top"
                wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              >
                <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">
                  {translations[langMode].createproacc}
                </span>
              </CommonButton>
            )}
          </div>
        </div>
        <div
          className={`w-full min-w-0 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-3 ${theme === "dark"
            ? "dark-mode rounded-md"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
          }`}
        >
          {/* Left: count, search, date range. Right: Reset, Filters. */}
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 lg:w-auto lg:flex-nowrap">
            <HoverTooltip
              text={translations[langMode]?.totalItemsTooltip ?? "Total number of records matching the current filters."}
              position="top"
              langMode={langMode}
              wrapperClassName="flex shrink-0"
            >
              <div className="flex h-9 px-2.5 sm:px-3 rounded-lg border border-slate-200 items-center gap-1.5 text-xs sm:text-sm shrink-0 text-slate-700 whitespace-nowrap">
                {itemsLoading ? "—" : totalItems} {translations[langMode].items}
              </div>
            </HoverTooltip>
            {/* Same field picker + input as User Management / TaskListView. */}
            <div className="flex h-9 min-w-0 flex-1 sm:min-w-[260px] sm:max-w-[360px]">
              <SearchFieldPicker
                options={scopeOptions}
                activeValue={searchScope}
                onSelect={handleScopeSelect}
                searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                isRtl={isRtl}
                buttonTestId="security-search-scope-button"
              />
              <div className="relative flex-1 min-w-0">
                <Search
                  size={15}
                  className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRtl ? "right-3" : "left-3"}`}
                />
                <input
                  id="account-search"
                  autoComplete="new-password"
                  type="text"
                  className={`block w-full h-9 text-sm text-slate-700 placeholder:text-slate-400 border border-slate-200 outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-9 pl-8 border-r-0 rounded-l-lg text-right" : "pl-9 pr-8 border-l-0 rounded-r-lg text-left"} ${theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                    }`}
                  placeholder={searchPlaceholder}
                  title={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                {inputValue && (
                  <button
                    type="button"
                    onClick={() => setInputValue("")}
                    aria-label={translations[langMode]?.clear ?? "Clear"}
                    className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 ${isRtl ? "left-2" : "right-2"}`}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
            <div ref={dateRangeButtonRef} className="relative h-9 w-full sm:w-auto">
              <HoverTooltip
                text={translations[langMode]?.exportDateRangeTooltip ?? "Select the date range for the export report"}
                position="top"
                langMode={langMode}
                wrapperClassName="relative inline-flex w-full sm:w-auto"
              >
                <button
                  type="button"
                  onClick={() => setShowDateRangePopover((v) => !v)}
                  className="h-9 w-full sm:w-auto px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 bg-white whitespace-nowrap"
                  style={{
                    borderColor: showDateRangePopover || hasDateRange ? "#93C5FD" : "#E2E8F0",
                    background: showDateRangePopover ? "#EFF6FF" : "white",
                    color: showDateRangePopover || hasDateRange ? "#1D4ED8" : "#334155",
                  }}
                >
                  <Calendar size={14} />
                  <span>
                    {hasDateRange
                      ? `${formatShortDate(fromDate) || "…"} – ${formatShortDate(toDate) || "…"}`
                      : translations[langMode]?.dateRange ?? "Date Range"}
                  </span>
                  <ChevronDown size={14} className={showDateRangePopover ? "rotate-180" : ""} />
                </button>
              </HoverTooltip>
              {showDateRangePopover && (
                <DateRangeFilterPopover
                  anchorEl={dateRangeButtonRef.current}
                  onClose={() => setShowDateRangePopover(false)}
                  fromDate={fromDate}
                  toDate={toDate}
                  onChangeFrom={(date) => {
                    setFromDate(date ? formatYmd(date) : "");
                    if (date && parseYmd(toDate) && parseYmd(toDate) < date) {
                      setToDate(formatYmd(date));
                    }
                  }}
                  onChangeTo={(date) => setToDate(date ? formatYmd(date) : "")}
                  onClear={() => {
                    setFromDate("");
                    setToDate("");
                    setShowDateRangePopover(false);
                  }}
                  onApply={() => setShowDateRangePopover(false)}
                  todayPicker={todayPicker}
                  isRtl={isRtl}
                  langMode={langMode}
                />
              )}
            </div>
          </div>
          <div className="flex w-full min-w-0 items-center gap-2 lg:w-auto shrink-0">
            <HoverTooltip
              text={translations[langMode]?.resetAllAppliedFiltersTooltip ?? "Clear all applied filters"}
              position="top"
              langMode={langMode}
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
            >
              <div className="relative w-full">
                <button data-button-intent="warning"
                  type="button"
                  onClick={handleResetAll}
                  disabled={!hasAnythingToReset}
                  className="h-9 w-full sm:w-auto px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 flex items-center justify-center gap-2 whitespace-nowrap transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={14} /> <span className="whitespace-nowrap">{translations[langMode].reset}</span>
                </button>
                {resetCount > 0 && (
                  <span
                    className={`absolute -top-2 ${isRtl ? "-left-2" : "-right-2"} grid min-w-[20px] h-5 px-1 place-items-center rounded-full bg-[#ff3b30] text-white text-[11px] font-semibold leading-[20px] tabular-nums pointer-events-none`}
                    aria-label={`${resetCount} applied filters`}
                  >
                    {resetCount > 99 ? "99+" : resetCount}
                  </span>
                )}
              </div>
            </HoverTooltip>
            <HoverTooltip
              text={translations[langMode]?.filtersToggleTooltip ?? "Apply filters from the table header"}
              position="top"
              langMode={langMode}
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
            >
              <button data-table-control="filters" aria-pressed={showColumnFilters}
                type="button"
                onClick={() => setShowColumnFilters((v) => !v)}
                className={`h-9 w-full sm:w-auto px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 whitespace-nowrap shrink-0 transition-colors ${showColumnFilters
                  ? "bg-blue-50 border-[#236DB4] text-[#236DB4]"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
              >
                <Filter size={14} /> <span className="whitespace-nowrap">{translations[langMode].columnFiltersLabel}</span>
              </button>
            </HoverTooltip>
          </div>
        </div>
      </div>

      {deleteModalOpen && deleteItemType === "single" && (
        <DeletePermission
          deleteFunction={() => deleteFunction(selectedRecords)}
          onClose={handleCloseDeleteModal}
          onDeleteText={translations[langMode].aresurewanttodeleterecord}
          confirmText={translations[langMode].delete}
          confirmVariant="danger"
        />
      )}
      <CreateAccountModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSaved={loadProtectedAccounts}
      />
    </>
  );
};
